import { ColDef, IRowNode, ValueGetterParams } from 'ag-grid-community';
import { CustomCellRendererProps } from 'ag-grid-react';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { Dispatch, SetStateAction } from 'react';

import { BomPartType } from 'bom/enums';
import { DEFAULT_PLACEHOLDER } from 'core/constants';
import { AutocompleteCellEditor, StatusCell, WarningTooltipForAgGrid } from 'core/elements';
import { AgGridFilterOption, AgGridTableHeaderLocation, WarningType } from 'core/enums';
import { AgGridDataRow } from 'core/types';
import { formatDate, getLabel, isObject } from 'core/utils';
import { VendorCell } from 'corporate/elements';
import { corporateService } from 'corporate/services';
import { PartDetailsTab } from 'parts/components/PartDetails';
import { CostSourceHelper, PricingHelper } from 'pricing/helpers';
import {
  commonColumns,
  getLineCellClass,
  getLineCellValue,
  QuotePartColField,
  QuotePartColId,
  QuotePartInlineActionDrawer,
} from 'quotes/components/QuotePartList';
import {
  CorporateLocationCell,
  CorporateLocationCellEditor,
  CostCell,
  CostSourceCell,
  ExtendedCostCell,
  ExtendedPriceCell,
  LockCell,
  NotesCell,
  PartContentCell,
  PnbPricingCell,
  PriceCell,
  PriceSourceCell,
  PricingDateCell,
  PricingDateCellEditor,
  QuantityCell,
  ShipmentTypeCell,
  UomCell,
  ValidityCell,
  ValidityErrorCell,
  VendorQuoteCell,
} from 'quotes/elements';
import {
  PricingStrategy,
  QuoteEntityType,
  QuotePartUnifiedStatus,
  QuoteType,
  QuoteVersionType,
  ValidationType,
} from 'quotes/enums';
import { QuotePartHelper } from 'quotes/helpers';
import { ICustomerQuotePart, IQuotePart, IQuotePartNote } from 'quotes/types';
import {
  getEffectiveSpaInfo,
  getPricingStrategyLabels,
  isBaseAdderPnbPricingEnabled,
  isCostPnbFixed,
} from 'quotes/utils';
import { Color } from 'theme/enums';

import { PricingStrategyCell } from './cells';
import {
  getCostCellValue,
  getPriceCellValue,
  getShippingLocationLabel,
  getVendorCellValue,
  isEditable,
  onMarginClick,
} from './utils';

interface IProps {
  isDataLoadCompleted: boolean;
  currentQuoteVersion: QuoteVersionType;
  openInlineActionDrawer: (quotePart: IQuotePart, action: QuotePartInlineActionDrawer) => void;
  setDefaultQuotePartDetailsTab?: Dispatch<SetStateAction<PartDetailsTab>>;
  minimumMarginThreshold?: number;
  hasTaxonomyColumn?: boolean;
  supplierEnabled?: boolean;
  quoteEntityType?: QuoteEntityType;
  setDraftVersionChanged?: () => void;
  hasRowReordering?: boolean;
  isAlternateVersionEnabled?: boolean;
  costDecimals?: number;
}

const getExtPurchaseCostNotes = (data: ICustomerQuotePart) => [data.costDescription, data.costNote];

export const columnDef = ({
  isDataLoadCompleted,
  currentQuoteVersion,
  openInlineActionDrawer,
  setDefaultQuotePartDetailsTab,
  minimumMarginThreshold,
  hasTaxonomyColumn = false,
  supplierEnabled = false,
  quoteEntityType = QuoteEntityType.QUOTE,
  setDraftVersionChanged,
  hasRowReordering = false,
  isAlternateVersionEnabled = false,
  costDecimals = PricingHelper.DEFAULT_COST_DECIMALS,
}: IProps): ColDef<AgGridDataRow>[] => {
  const isRowEditable = ({ data }: { data: ICustomerQuotePart }) =>
    isEditable({ data, currentQuoteVersion });

  const isLockAllowed = ({ data }: { data: ICustomerQuotePart }) => {
    return (
      QuoteVersionType.DRAFT === currentQuoteVersion &&
      BomPartType.DISCOUNT !== data.type &&
      !data.noQuote &&
      !data.deleted
    );
  };

  const isDescriptionEditable = ({ data }) =>
    isRowEditable({ data }) && BomPartType.PART === data.type && !data.partArr?.verified;

  const isPricingEditable = ({ data }) => isRowEditable({ data }) && !data.partArr?.wildcard;

  const isPriceEditable = ({ data }: { data: ICustomerQuotePart }) =>
    isAlternateVersionEnabled
      ? isPricingEditable({ data }) && 0 === data.externalPriceLocked
      : isPricingEditable({ data });

  const isPnbMarginEditable = ({ data }: { data: ICustomerQuotePart }) =>
    isRowEditable({ data }) && isBaseAdderPnbPricingEnabled(data) && !isCostPnbFixed(data);

  const isUomEditable = ({ data }: { data: ICustomerQuotePart }) => {
    switch (quoteEntityType) {
      case QuoteEntityType.ORDER:
        return isRowEditable({ data }) && (isAlternateVersionEnabled || !data.verified);
      case QuoteEntityType.QUOTE:
      default:
        return isRowEditable({ data });
    }
  };

  const handleOnMarginClick = (quotePart: ICustomerQuotePart) =>
    onMarginClick(quotePart, (drawer: QuotePartInlineActionDrawer) =>
      openInlineActionDrawer(quotePart, drawer)
    );

  const openPricingCalculator = (data: ICustomerQuotePart) =>
    isPricingEditable({ data }) && handleOnMarginClick(data);

  const onUnitCostClick = ({ data }: { data: ICustomerQuotePart }) =>
    isAlternateVersionEnabled && openPricingCalculator(data);

  const onReplacementCostClick = ({ data }: { data: ICustomerQuotePart }) =>
    data?.supplier?.isShipAndDebit && openPricingCalculator(data);

  const isRowSelectPartAllowed = ({ data }: { data: ICustomerQuotePart }) => {
    return (
      QuoteVersionType.DRAFT === currentQuoteVersion &&
      !data.substituted &&
      !data.noQuote &&
      !data.locked &&
      !data.deleted
    );
  };

  const {
    aliasColumn,
    allInventoryColumn,
    alternateColumn,
    alternateVendorPartNumberColumn,
    customerPartNumberColumn,
    datasheetColumn,
    descriptionColumn,
    imageColumn,
    inventoryStatusColumn,
    lineTypeColumn,
    manufacturerColumn,
    locationInventoryColumn,
    packagingColumn,
    statusColumn,
    taxonomyColumn,
  } = commonColumns(
    hasTaxonomyColumn,
    openInlineActionDrawer,
    setDefaultQuotePartDetailsTab,
    isDescriptionEditable
  );

  const vendorColumn = supplierEnabled
    ? {
        colId: QuotePartColId.VENDOR,
        headerName: t('headerVendor'),
        field: 'companyManufacturerName',
        filter: 'agTextColumnFilter',
        minWidth: 130,
        maxWidth: 400,
        valueGetter: ({ data }: { data: ICustomerQuotePart }) => getVendorCellValue(data),
        onCellClicked: ({ data }: { data: ICustomerQuotePart }) =>
          openInlineActionDrawer(data, QuotePartInlineActionDrawer.ASSIGN_VENDOR),
        suppressPaste: true,
        suppressFillHandle: true,
        enableRowGroup: true,
        cellRenderer: VendorCell,
        cellRendererParams: { isAlternateVersionEnabled, isRowEditable },
      }
    : null;

  const poLineNumberColumn =
    QuoteEntityType.ORDER === quoteEntityType
      ? {
          colId: QuotePartColId.CUSTOMER_PO_LINE_NUMBER,
          headerName: t('headerCustomerPoLineNumber'),
          field: 'customerPoLineNumber',
          filter: 'agTextColumnFilter',
          editable: isRowEditable,
          suppressPaste: true,
          suppressFillHandle: true,
        }
      : null;

  const externalSpaCostColumns = [];

  if (isAlternateVersionEnabled) {
    externalSpaCostColumns.push(
      {
        colId: QuotePartColId.EXTERNAL_SPA_COST,
        headerName: t('headerExternalSpaCost'),
        valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
          data && getEffectiveSpaInfo(data).pricingData.externalSpaCost,
        filter: 'agNumberColumnFilter',
        maxWidth: 200,
        enableRowGroup: true,
        suppressFillHandle: true,
        valueFormatter: ({ value }: { value: number }) =>
          PricingHelper.formatPricing({
            amount: value,
            displayZero: true,
            decimals: costDecimals,
            fixedDecimals: true,
          }),
      },
      {
        colId: QuotePartColId.EXTERNAL_SPA_COST_SOURCE,
        headerName: t('headerExternalSpaCostSource'),
        valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
          data && getEffectiveSpaInfo(data).pricingData.externalSpaCostSource,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        suppressFillHandle: true,
      },
      {
        colId: QuotePartColId.EXTERNAL_SPA_COST_SOURCE_NAME,
        headerName: t('headerExternalSpaCostSourceName'),
        valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
          data && getEffectiveSpaInfo(data).pricingData.externalSpaCostSourceName,
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        suppressFillHandle: true,
        minWidth: 120,
        maxWidth: 240,
        cellRendererParams: {
          textEllipsis: false,
        },
      },
      {
        colId: QuotePartColId.EXTERNAL_SPA_COST_REG_NUMBER,
        headerName: t('headerExternalSpaCostRegistrationNumber'),
        valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
          data && getEffectiveSpaInfo(data).pricingData.externalSpaCostRegistrationNum,
        filter: 'agTextColumnFilter',
        maxWidth: 240,
        enableRowGroup: true,
        suppressFillHandle: true,
      },
      {
        colId: QuotePartColId.EXTERNAL_SPA_COST_END_DATE,
        headerName: t('headerExternalSpaCostEndDate'),
        valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
          data && getEffectiveSpaInfo(data).pricingData.externalSpaCostEndDate,
        maxWidth: 240,
        enableRowGroup: true,
        suppressFillHandle: true,
        filter: 'agDateColumnFilter',
        filterParams: {
          defaultOption: AgGridFilterOption.GREATER_THAN,
          includeBlanksInGreaterThan: true,
        },
        filterValueGetter: ({ data }: { data: ICustomerQuotePart }) => {
          const endDate = data && getEffectiveSpaInfo(data).pricingData.externalSpaCostEndDate;

          return endDate && new Date(endDate as string);
        },
        valueFormatter: ({ value }: { value: string }) => formatDate(value),
      }
    );
  }

  const replacementCostColumn = isAlternateVersionEnabled
    ? {
        colId: QuotePartColId.REPLACEMENT_COST,
        headerName: t('headerReplacementCost'),
        field: 'replacementCost',
        filter: 'agNumberColumnFilter',
        filterParams: {
          defaultOption: AgGridFilterOption.IN_RANGE,
        },
        enableRowGroup: true,
        enableValue: true,
        enablePivot: true,
        suppressPaste: true,
        suppressFillHandle: true,
        minWidth: 140,
        maxWidth: 220,
        cellStyle: { alignItems: 'flex-end' },
        onCellClicked: onReplacementCostClick,
        valueGetter: ({ data }: { data: ICustomerQuotePart }) => {
          if (!data || !data.supplier?.isShipAndDebit) {
            return null;
          }

          return data.replacementCost ?? data.externalReplacementCost;
        },
        valueFormatter: ({ value }: { value: number }) =>
          value == null
            ? DEFAULT_PLACEHOLDER
            : PricingHelper.formatPricing({
                amount: value,
                displayZero: true,
                decimals: costDecimals,
                fixedDecimals: true,
              }),
      }
    : null;

  const replacementCostNumberColumn = isAlternateVersionEnabled
    ? {
        colId: QuotePartColId.REPLACEMENT_COST_NUMBER,
        headerName: t('labelReplacementCostNumber'),
        field: 'replacementCostNumber',
        filter: 'agTextColumnFilter',
        enableRowGroup: true,
        suppressPaste: true,
        suppressFillHandle: true,
        minWidth: 140,
        maxWidth: 240,
        onCellClicked: onReplacementCostClick,
        valueGetter: ({ data }: { data: ICustomerQuotePart }) => {
          if (!data || !data.supplier?.isShipAndDebit) {
            return null;
          }

          return data.replacementCostNumber || data.externalRepCostRegistrationNum;
        },
        valueFormatter: ({ value }: { value: string }) => value || DEFAULT_PLACEHOLDER,
      }
    : null;

  const unifiedPartStatusColumn = isAlternateVersionEnabled
    ? {
        colId: QuotePartColId.UNIFIED_PART_STATUS,
        headerName: t('headerUnifiedPartStatus'),
        field: 'ddpItemFlag',
        enableRowGroup: true,
        suppressFillHandle: true,
        minWidth: 105,
        width: 105,
        maxWidth: 105,
        headerClass: 'paddingSmall',
        cellClass: 'paddingSmall noDecorationCell',
        filter: 'agSetColumnFilter',
        filterParams: {
          valueFormatter: ({ value }: { value: QuotePartUnifiedStatus }) =>
            value === QuotePartUnifiedStatus.ACTIVE ? t('labelActive') : t('labelInactive'),
          values: [QuotePartUnifiedStatus.ACTIVE, QuotePartUnifiedStatus.INACTIVE],
        },
        valueGetter: ({ data }: { data: ICustomerQuotePart }) => {
          if (!data) {
            return null;
          }

          return data?.ddpItemFlag
            ? QuotePartUnifiedStatus.ACTIVE
            : QuotePartUnifiedStatus.INACTIVE;
        },
        cellRenderer: StatusCell,
        cellRendererParams: {
          colorMap: {
            [QuotePartUnifiedStatus.ACTIVE]: Color.PRIMARY,
            [QuotePartUnifiedStatus.INACTIVE]: Color.WARNING,
          },
        },
      }
    : null;

  return [
    {
      colId: QuotePartColId.LINE,
      headerName: '#',
      pinned: 'left',
      field: 'line',
      wrapText: false,
      suppressPaste: true,
      suppressFillHandle: true,
      suppressSizeToFit: false,
      suppressHeaderMenuButton: true,
      resizable: false,
      minWidth: 60,
      width: 60,
      maxWidth: 60,
      suppressColumnsToolPanel: true,
      suppressMovable: true,
      lockPinned: true,
      enableValue: true,
      enablePivot: true,
      filter: 'agNumberColumnFilter',
      floatingFilter: false,
      valueGetter: ({ data }: { data: ICustomerQuotePart }) => getLineCellValue(data),
      rowDrag: ({ node, data }: { node: IRowNode<ICustomerQuotePart>; data: ICustomerQuotePart }) =>
        hasRowReordering &&
        isDataLoadCompleted &&
        !node?.group &&
        !data.secondaryLine &&
        !data.deleted,
      headerClass: 'paddingSmall',
      cellClass: ({ data }: { data: ICustomerQuotePart }) => getLineCellClass(data),
      cellRendererParams: { placeholder: '' },
    },
    {
      colId: QuotePartColId.LOCKED,
      headerName: t('headerLock'),
      headerValueGetter: ({ location }: { location: AgGridTableHeaderLocation }) =>
        AgGridTableHeaderLocation.HEADER === location ? '' : t('headerLock'),
      field: 'locked',
      filter: 'agSetColumnFilter',
      floatingFilter: false,
      filterParams: {
        valueFormatter: ({ value }: { value: boolean }) =>
          value ? t('labelLocked') : t('labelUnlocked'),
      },
      minWidth: 62,
      width: 62,
      maxWidth: 62,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      pinned: 'left',
      suppressMovable: true,
      lockPinned: true,
      headerClass: 'paddingSmall',
      cellClass: 'paddingSmall',
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: LockCell,
      cellRendererParams: {
        onEditPartLineClick: (quotePart: IQuotePart) =>
          openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.EDIT_LINE),
        isRowEditable,
        isLockAllowed,
      },
    },
    statusColumn,
    unifiedPartStatusColumn,
    imageColumn,
    {
      colId: QuotePartColId.PART_NUMBER,
      headerName: t('headerPartNumber'),
      filter: 'agTextColumnFilter',
      field: 'partArr.number',
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 160,
      maxWidth: 400,
      enableValue: true,
      enablePivot: true,
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && QuotePartHelper.getDisplayedPartNumber(data),
      filterValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data &&
        [
          QuotePartHelper.getDisplayedPartNumber(data),
          data.vendorPartNumber,
          data.alternateVendorPartNumber,
        ]
          .filter(Boolean)
          .join(' '),
      cellRenderer: PartContentCell,
      cellRendererParams: {
        onPartNumberClick: (quotePart: IQuotePart) =>
          openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.DETAILS),
        onEditPartLineClick: (quotePart: IQuotePart) =>
          openInlineActionDrawer(quotePart, QuotePartInlineActionDrawer.EDIT_LINE),
        isRowEditable,
        isRowSelectPartAllowed,
        setDraftVersionChanged,
      },
    },
    alternateVendorPartNumberColumn,
    manufacturerColumn,
    descriptionColumn,
    {
      colId: QuotePartColId.QUANTITY,
      headerName: t('headerQuantity'),
      field: QuotePartColField.QUANTITY,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sumQty',
      minWidth: 95,
      cellStyle: { alignItems: 'flex-end' },
      editable: isRowEditable,
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: ({ data }: { data: ICustomerQuotePart }) => ({
        type: QuotePartHelper.quantityWarningType(data),
      }),
      tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        QuotePartHelper.quantityValidationWarnings(data),
      cellRenderer: QuantityCell,
    },
    {
      colId: QuotePartColId.UOM,
      headerName: t('headerUom'),
      field: QuotePartColField.UOM,
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      minWidth: 75,
      suppressPaste: true,
      suppressFillHandle: true,
      editable: isUomEditable,
      cellEditorSelector: ({ data }: { data: ICustomerQuotePart }) => ({
        component: 'agRichSelectCellEditor',
        params: {
          values: data?.allowedUoms,
        },
      }),
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: ({ data }: { data: ICustomerQuotePart }) => ({
        type: QuotePartHelper.hasUomWarning(data) ? WarningType.ERROR : null,
      }),
      tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        QuotePartHelper.uomWarnings(data),
      cellRenderer: UomCell,
    },
    {
      colId: QuotePartColId.NOTES,
      headerName: t('headerNotes'),
      filter: 'agTextColumnFilter',
      field: 'defaultNotes',
      valueGetter: ({ data }: { data: ICustomerQuotePart }) => data?.notes,
      sortable: true,
      minWidth: 400,
      width: 400,
      maxWidth: 600,
      cellRenderer: NotesCell,
      filterParams: {
        textFormatter: (data: Record<string, IQuotePartNote> | string) =>
          isObject(data)
            ? Object.values(data)
                .map((note: IQuotePartNote) => note.content)
                .join(' ')
                .toLowerCase()
            : data.toLowerCase(),
      },
      cellRendererParams: {
        isRowEditable,
        entityType: quoteEntityType,
        quoteType: QuoteType.CUSTOMER,
      },
    },
    {
      colId: QuotePartColId.COST,
      headerName: t('headerUnitCost'),
      field: QuotePartColField.COMPUTED_PURCHASE_COST,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      cellClass: 'paddingSmallLeft',
      editable: ({ data }: { data: ICustomerQuotePart }) =>
        isAlternateVersionEnabled ? false : isPricingEditable({ data }),
      onCellClicked: onUnitCostClick,
      minWidth: 200,
      maxWidth: 350,
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: ({ data }: { data: ICustomerQuotePart }) => ({
        type: QuotePartHelper.costWarningType(data),
      }),
      tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        QuotePartHelper.costValidationWarnings(data),
      cellRenderer: CostCell,
      cellRendererParams: { quoteEntityType, costDecimals },
    },
    {
      colId: QuotePartColId.PRICE,
      headerName: t('headerUnitPrice'),
      field: QuotePartColField.PRICE,
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      editable: isPriceEditable,
      minWidth: 200,
      maxWidth: 350,
      cellClass: 'paddingSmall',
      cellStyle: { alignItems: 'flex-end' },
      valueGetter: ({ data, api, node, column }: ValueGetterParams) => {
        const isEditing = api
          .getEditingCells()
          ?.some(
            (cell) =>
              cell.rowIndex === node.rowIndex && cell.column.getColId() === column.getColId()
          );

        if (isEditing) {
          return data?.price;
        }

        return data?.includeFreight ? data?.priceWithFreight : data?.price;
      },
      cellEditorParams: ({ data }: { data: ICustomerQuotePart }) => {
        return {
          value: data?.price,
        };
      },
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: ({ data }: { data: ICustomerQuotePart }) => ({
        type: QuotePartHelper.marginWarningType(data),
      }),
      tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        QuotePartHelper.marginValidationWarnings(data),
      cellRenderer: PriceCell,
      cellRendererParams: {
        onMarginClick: handleOnMarginClick,
        isPricingEditable,
        minimumMarginThreshold,
      },
    },
    isAlternateVersionEnabled
      ? (() => {
          const pricingStrategyLabels = getPricingStrategyLabels();
          const pricingStrategyValues = [PricingStrategy.EXTERNAL, PricingStrategy.PRICING_PROFILE];

          return {
            colId: QuotePartColId.PRICING_STRATEGY,
            headerName: t('headerPricingStrategy'),
            field: 'pricingStrategy',
            filter: 'agSetColumnFilter',
            filterParams: {
              values: pricingStrategyValues,
              valueFormatter: ({ value }: { value: string }) =>
                pricingStrategyLabels[value as PricingStrategy] ?? '',
            },
            enableRowGroup: true,
            suppressPaste: true,
            suppressFillHandle: true,
            minWidth: 160,
            maxWidth: 220,
            editable: isRowEditable,
            cellEditorSelector: () => ({
              component: 'agRichSelectCellEditor',
              params: {
                values: pricingStrategyValues,
                formatValue: (value: PricingStrategy) => pricingStrategyLabels[value] ?? '',
              },
            }),
            cellRenderer: PricingStrategyCell,
          };
        })()
      : null,
    {
      colId: QuotePartColId.EXT_PRICE,
      headerName: t('headerExtPrice'),
      field: 'extendedPrice',
      width: 80,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data?.includeFreight ? data?.extendedPriceWithFreight : data?.extendedPrice,
      cellStyle: { alignItems: 'flex-end' },
      cellRenderer: ExtendedPriceCell,
      cellRendererParams: { costDecimals },
    },
    {
      colId: QuotePartColId.EXT_PURCHASE_COST,
      headerName: t('headerExtPurchaseCost'),
      field: 'extendedPurchaseCost',
      minWidth: 140,
      maxWidth: 180,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      cellStyle: { alignItems: 'flex-end' },
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && (data.computedPurchaseCost || 0) * (data.quantity || 0),
      cellRenderer: ExtendedCostCell,
      cellRendererParams: {
        columnId: QuotePartColId.EXT_PURCHASE_COST,
        getNotes: getExtPurchaseCostNotes,
        costDecimals,
      },
    },
    {
      colId: QuotePartColId.EXT_STANDARD_COST,
      headerName: t('headerExtStandardCost'),
      field: 'extendedStandardCost',
      minWidth: 140,
      maxWidth: 180,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agNumberColumnFilter',
      enableValue: true,
      enablePivot: true,
      aggFunc: 'sum',
      cellStyle: { alignItems: 'flex-end' },
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && (data.computedCost || 0) * (data.quantity || 0),
      cellRenderer: ExtendedCostCell,
      cellRendererParams: {
        columnId: QuotePartColId.EXT_STANDARD_COST,
        costDecimals,
      },
    },
    isAlternateVersionEnabled
      ? {
          colId: QuotePartColId.PRICING_DATE,
          headerName: t('headerPricingDate'),
          field: 'pricingDate',
          cellDataType: false,
          filter: 'agDateColumnFilter',
          filterParams: {
            defaultOption: AgGridFilterOption.GREATER_THAN,
            includeBlanksInGreaterThan: true,
          },
          valueFormatter: ({ value }: { value: string }) => formatDate(value),
          enableRowGroup: true,
          suppressPaste: true,
          suppressFillHandle: true,
          minWidth: 160,
          maxWidth: 240,
          editable: true,
          cellEditor: PricingDateCellEditor,
          cellRenderer: PricingDateCell,
          tooltipComponent: WarningTooltipForAgGrid,
          tooltipComponentParams: ({ data }: { data: ICustomerQuotePart }) => ({
            type:
              data && data.pricingDate && dayjs(data.pricingDate).isBefore(dayjs(), 'day')
                ? WarningType.WARNING
                : null,
          }),
          tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) => {
            if (!data || !data.pricingDate) {
              return null;
            }

            return dayjs(data.pricingDate).isBefore(dayjs(), 'day')
              ? t('warningPricingDateExpired')
              : null;
          },
        }
      : null,
    locationInventoryColumn,
    allInventoryColumn,
    inventoryStatusColumn,
    taxonomyColumn,
    {
      colId: QuotePartColId.SHIPMENT_TYPE,
      headerName: t('headerShipmentType'),
      field: QuotePartColField.SHIPMENT_TYPE,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) => value && value?.name,
      },
      editable: isRowEditable,
      valueParser: ({ value }) => ({ id: value }),
      valueFormatter: ({ value }) => value?.name,
      keyCreator: ({ value }) => value?.id,
      cellRenderer: ShipmentTypeCell,
      cellEditor: AutocompleteCellEditor,
      cellEditorParams: ({ data }: CustomCellRendererProps) => ({
        autocompleteProps: {
          getOptions: corporateService.getShipmentTypeAutocomplete,
          requestPayload: { quotePart: { id: data?.id }, filters: { deleted: 0 } },
          disableClearable: true,
        },
      }),
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: () => ({
        type: WarningType.ERROR,
      }),
      tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        QuotePartHelper.shipmentTypeWarning(data).label,
    },
    {
      colId: QuotePartColId.SHIPPING_LOCATION,
      headerName: t('headerShippingLocation'),
      field: QuotePartColField.CORPORATE_LOCATION,
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 150,
      maxWidth: 300,
      cellStyle: { alignItems: 'flex-start !important' },
      filter: 'agSetColumnFilter',
      filterParams: {
        valueFormatter: ({ value }) =>
          value && getShippingLocationLabel(value, isAlternateVersionEnabled),
      },
      editable: isRowEditable,
      valueParser: ({ value }) => ({ id: value }),
      valueFormatter: ({ value }) =>
        [
          value?.addressName || value?.name,
          value?.vendorIdentifier ? `(${value?.vendorIdentifier})` : '',
        ]
          .filter(Boolean)
          .join(' '),
      keyCreator: ({ value }) => value?.id,
      cellRenderer: CorporateLocationCell,
      cellRendererParams: { isAlternateVersionEnabled },
      cellEditor: CorporateLocationCellEditor,
      cellEditorParams: { isAlternateVersionEnabled },
      tooltipComponent: WarningTooltipForAgGrid,
      tooltipComponentParams: () => ({
        type: WarningType.ERROR,
      }),
      tooltipValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        QuotePartHelper.shippingLocationValidationWarnings(data),
    },
    {
      colId: QuotePartColId.VALIDITY,
      headerName: t('headerValidity'),
      enableRowGroup: true,
      enableValue: true,
      enablePivot: true,
      filter: 'agSetColumnFilter',
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && QuotePartHelper.validity(data),
      filterValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && getLabel(QuotePartHelper.validity(data)),
      autoHeight: true,
      suppressPaste: true,
      suppressFillHandle: true,
      cellRenderer: ValidityCell,
    },
    {
      colId: QuotePartColId.VALIDATION_ERRORS,
      headerName: t('headerValidityErrors'),
      filter: 'agSetColumnFilter',
      filterValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data &&
        QuotePartHelper.validationErrors({
          data,
          statuses: Object.values(ValidationType),
          withValues: false,
          filteredByStatuses: false,
          quoteEntityType,
        }),
      autoHeight: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 150,
      maxWidth: 240,
      cellRenderer: ValidityErrorCell,
      cellRendererParams: { quoteEntityType },
    },
    {
      colId: QuotePartColId.COST_SOURCE,
      headerName: t('headerCostSource'),
      valueGetter: ({ data }: { data: ICustomerQuotePart }) => getCostCellValue(data),
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      suppressFillHandle: true,
      minWidth: 120,
      maxWidth: 240,
      cellRenderer: CostSourceCell,
    },
    {
      colId: QuotePartColId.VENDOR_QUOTE,
      headerName: t('headerVendorQuote'),
      field: 'costSourceQuoteId',
      filter: 'agSetColumnFilter',
      enableRowGroup: true,
      suppressPaste: true,
      suppressFillHandle: true,
      width: 150,
      maxWidth: 200,
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        CostSourceHelper.getVendorQuoteNumber(data),
      cellRenderer: VendorQuoteCell,
    },
    ...externalSpaCostColumns,
    replacementCostColumn,
    replacementCostNumberColumn,
    {
      colId: QuotePartColId.PRICE_SOURCE,
      headerName: t('headerPriceSource'),
      valueGetter: ({ data }: { data: ICustomerQuotePart }) => getPriceCellValue(data),
      filter: 'agTextColumnFilter',
      enableRowGroup: true,
      suppressPaste: true,
      suppressFillHandle: true,
      minWidth: 120,
      maxWidth: 240,
      cellRenderer: PriceSourceCell,
    },
    {
      colId: QuotePartColId.BASE,
      headerName: t('headerBase'),
      field: 'priceBase',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      width: 120,
      maxWidth: 300,
      hide: true,
      cellStyle: { alignItems: 'flex-end' },
      onCellDoubleClicked: ({ data }: { data: ICustomerQuotePart }) =>
        isPnbMarginEditable({ data }) &&
        openInlineActionDrawer(data, QuotePartInlineActionDrawer.UPDATE_PNB_MARGIN),
      cellRenderer: PnbPricingCell,
      cellRendererParams: {
        onMarginClick: handleOnMarginClick,
        isRowEditable,
        costDecimals,
      },
    },
    {
      colId: QuotePartColId.ADDER,
      headerName: t('headerAdder'),
      field: 'priceAdder',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      width: 120,
      maxWidth: 300,
      hide: true,
      cellStyle: { alignItems: 'flex-end' },
      onCellDoubleClicked: ({ data }: { data: ICustomerQuotePart }) =>
        isPnbMarginEditable({ data }) &&
        openInlineActionDrawer(data, QuotePartInlineActionDrawer.UPDATE_PNB_MARGIN),
      cellRenderer: PnbPricingCell,
      cellRendererParams: {
        type: 'adder',
        onMarginClick: handleOnMarginClick,
        isRowEditable,
        costDecimals,
      },
    },
    packagingColumn,
    {
      colId: QuotePartColId.LIST_PRICE,
      headerName: t('headerListPrice'),
      field: 'corporatePricing.listPrice',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      filterValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data?.corporatePricing?.listPrice,
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && PricingHelper.formatPricingPerUom(data?.corporatePricing?.listPrice, data?.uom),
    },
    {
      colId: QuotePartColId.FLOOR_PRICE,
      headerName: t('headerFloorPrice'),
      field: 'corporatePricing.floorPrice',
      filter: 'agNumberColumnFilter',
      filterParams: {
        defaultOption: AgGridFilterOption.IN_RANGE,
      },
      enableValue: true,
      enablePivot: true,
      suppressPaste: true,
      suppressFillHandle: true,
      filterValueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data?.corporatePricing?.floorPrice,
      valueGetter: ({ data }: { data: ICustomerQuotePart }) =>
        data && PricingHelper.formatPricingPerUom(data?.corporatePricing?.floorPrice, data?.uom),
    },
    alternateColumn,
    customerPartNumberColumn,
    vendorColumn,
    aliasColumn,
    lineTypeColumn,
    datasheetColumn,
    poLineNumberColumn,
  ].filter((column) => !!column) as ColDef[];
};
